import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table, Button, Container, Spinner, Form, Pagination, Tabs, Tab } from "react-bootstrap";
import userService from "../services/user.service";

// Die Basis-URL für die Anzeige von PDFs aus der .env-Datei
const showURI = process.env.REACT_APP_API_URL;

// =================================================================
// Komponente für RECHNUNGSAUSGANG (Ihre bisherige Logik)
// Zeigt Rechnungen, die dieser Subvo an seine Kunden erstellt hat.
// =================================================================
const OutgoingInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("invoice_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const seller_id = 1; // In einer realen App wäre dies die ID des eingeloggten Subvo-Benutzers

  const loadInvoices = useCallback(async () => {
    setApiErrors("");
    setLoading(true);
    try {
      const response = await userService.getInvoicesSUBVO({
        page: currentPage,
        search: searchTerm,
        sort: sortField,
        order: sortOrder,
        seller_id: seller_id,
      });
      setInvoices(response.data.invoices);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setApiErrors("Ausgangsrechnungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, sortField, sortOrder, seller_id]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await userService.downloadInvoiceSUBVO(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      let filename = `rechnung_${invoiceId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1].replace(/(\.pdf)[\W_]+$/, '$1');
        }
      }
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      setApiErrors("Fehler beim Herunterladen der Rechnung.");
    }
  };

  // KORREKTUR: Der Name der Funktion muss hier "viewInvoice" sein.
  const viewInvoice = (invoiceId) => {
      // Die neue URL, die auf unseren HTML-Endpunkt zeigt
      const htmlUrl = `${showURI}subvo/invoices/incoming/${invoiceId}/html/view`;
      window.open(htmlUrl, '_blank');
  };

  return (
    <>
      <Form.Group controlId="searchOutgoing" className="mb-3">
        <Form.Control
          type="text"
          placeholder="Ausgangsrechnungen durchsuchen..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Form.Group>
      {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
      {loading && <div className="text-center my-3"><Spinner animation="border" variant="primary" /> <span className="ms-2">Lade Ausgangsrechnungen...</span></div>}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th onClick={() => handleSortChange("invoice_date")}>Datum</th>
            <th onClick={() => handleSortChange("invoice_number")}>Rechnungs-Nr.</th>
            <th onClick={() => handleSortChange("buyer_name")}>Kundenname</th>
            <th onClick={() => handleSortChange("invoice_brut_total")}>Bruttobetrag</th>
            <th onClick={() => handleSortChange("provision_brut_total")}>Provision</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {invoices.length > 0 ? (
            invoices.map((invoice) => (
              <tr key={invoice.invoice_id}>
                <td>{new Date(invoice.invoice_date).toLocaleDateString('de-DE')}</td>
                <td>{invoice.invoice_number}</td>
                <td>{invoice.buyer_name || invoice.buyer_id}</td>
                <td>{invoice.invoice_brut_total} {invoice.currency}</td>
                <td>{invoice.provision_brut_total} {invoice.currency}</td>
                <td>
                  <Button variant="info" onClick={() => viewInvoice(invoice.invoice_id)} size="sm" className="me-2 mb-1">Anzeigen</Button>
                  <Button variant="primary" onClick={() => downloadInvoice(invoice.invoice_id)} size="sm" className="mb-1">Laden</Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center">Keine Ausgangsrechnungen gefunden.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination className="justify-content-center">
          {[...Array(totalPages)].map((_, index) => (
            <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => setCurrentPage(index + 1)}>
              {index + 1}
            </Pagination.Item>
          ))}
        </Pagination>
      )}
    </>
  );
}; // <-- KORREKTUR 1: Fehlende schließende Klammer hinzugefügt

// =================================================================
// Komponente für RECHNUNGSEINGANG (Logik aus M5InvoiceManager)
// Zeigt Rechnungen, die dieser Subvo von seinem Mainvo erhalten hat.
// =================================================================
const IncomingInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiErrors, setApiErrors] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('invoice_number');
    const [sortOrder, setSortOrder] = useState('asc');

    const translateStatus = (status) => {
        const statusMap = {
            pending: 'Ausstehend',
            paid: 'Bezahlt',
            canceled: 'Storniert',
            overdue: 'Überfällig',
            refunded: 'Erstattet'
        };
        return statusMap[status] || status;
    };

    const loadInvoices = useCallback(async () => {
      setLoading(true);
      setApiErrors('');
      try {
        // FINALE KORREKTUR: Wir rufen die neu erstellte Funktion auf.
        const response = await userService.getIncomingInvoicesForSubvo({
          page: currentPage,
          search: searchTerm,
          sort: sortField,
          order: sortOrder,
        });
        setInvoices(response.data.invoices);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        let errorMessage = "Eingangsrechnungen konnten nicht geladen werden.";
        if (error.response && error.response.status === 403) {
          errorMessage = "Zugriff verweigert. Sie haben keine Berechtigung, diese Rechnungen anzuzeigen.";
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        }
        setApiErrors(errorMessage);
      } finally {
        setLoading(false);
      }
    }, [currentPage, searchTerm, sortField, sortOrder]);

    useEffect(() => {
      loadInvoices();
    }, [loadInvoices]);

    const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
      setCurrentPage(1);
    };

    const handleSortChange = (field) => {
      const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
      setSortField(field);
      setSortOrder(newSortOrder);
    };

    const handleDownloadInvoice = async (invoiceId) => {
      setLoading(true);
      setApiErrors('');
      try {
        // FINALE KORREKTUR: Wir nutzen die neue Download-Funktion.
        const response = await userService.downloadIncomingInvoiceForSubvo(invoiceId);
        const contentDisposition = response.headers['content-disposition'];
        let filename = `rechnung_${invoiceId}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1].replace(/(\.pdf)[\W_]+$/, '$1');
          }
        }
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        setApiErrors(`Rechnung konnte nicht heruntergeladen werden: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const viewInvoice = (invoiceId) => {
        // Die neue URL, die auf unseren HTML-Endpunkt zeigt
        const htmlUrl = `${showURI}subvo/invoices/incoming/${invoiceId}/html/view`;
        window.open(htmlUrl, '_blank');
    };

    return (
        <>
            <Form.Group controlId="searchIncoming" className="mb-3">
                <Form.Control
                type="text"
                placeholder="Eingangsrechnungen durchsuchen..."
                value={searchTerm}
                onChange={handleSearchChange}
                />
            </Form.Group>
            {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
            {loading && <div className="text-center my-3"><Spinner animation="border" variant="primary" /> <span className="ms-2">Lade Eingangsrechnungen...</span></div>}
            <Table striped bordered hover responsive>
                <thead>
                <tr>
                    <th onClick={() => handleSortChange("invoice_date")}>Datum</th>
                    <th onClick={() => handleSortChange("invoice_number")}>Rechnungs-Nr.</th>
                    <th onClick={() => handleSortChange("seller_name")}>Aussteller</th>
                    <th onClick={() => handleSortChange("invoice_brut_total")}>Bruttobetrag</th>
                    <th onClick={() => handleSortChange("status")}>Status</th>
                    <th>Aktionen</th>
                </tr>
                </thead>
                <tbody>
                {invoices.length > 0 ? invoices.map((invoice) => (
                    <tr key={invoice.invoice_id}>
                    <td>{new Date(invoice.invoice_date).toLocaleDateString('de-DE')}</td>
                    <td>{invoice.invoice_number}</td>
                    <td>{invoice.seller_name || 'Unbekannt'}</td>
                    <td>{invoice.invoice_brut_total} {invoice.currency}</td>
                    <td style={{color: invoice.status === 'paid' ? 'green' : (invoice.status === 'pending' ? 'orange' : 'black')}}>
                        {translateStatus(invoice.status)}
                    </td>
                    <td>
                        
                        <Button 
                            variant="info" 
                            // KORREKTUR: Der Funktionsaufruf hier muss ebenfalls "viewInvoice" sein.
                            onClick={() => viewInvoice(invoice.invoice_id)} 
                            size="sm" 
                            className="me-2 mb-1">
                            Anzeigen
                        </Button>




                        <Button
                            variant="primary"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleDownloadInvoice(invoice.invoice_id)}
                        >
                            Laden
                        </Button>
                    </td>
                    </tr>
                )) : (
                    <tr>
                    <td colSpan="6" className="text-center">Keine Eingangsrechnungen gefunden.</td>
                    </tr>
                )}
                </tbody>
            </Table>
            {totalPages > 1 && (
                <Pagination className="justify-content-center">
                    {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => setCurrentPage(index + 1)}>
                        {index + 1}
                    </Pagination.Item>
                    ))}
                </Pagination>
            )}
        </>
    );
}; // <-- KORREKTUR 2: Fehlende schließende Klammer hinzugefügt

// =================================================================
// Hauptkomponente, die die Tabs rendert und exportiert wird
// =================================================================
const InvoiceManagerSUBVO = () => {
    return (
      <Container fluid className="mt-4">
        <h1 className="mb-4">Meine Rechnungen</h1>
        <Tabs defaultActiveKey="outgoing" id="invoice-subvo-tabs" className="mb-3" justify>
          <Tab eventKey="outgoing" title="Rechnungsausgang">
            <OutgoingInvoices />
          </Tab>
          <Tab eventKey="incoming" title="Rechnungseingang">
            <IncomingInvoices />
          </Tab>
        </Tabs>
      </Container>
    );
  };
  
export default InvoiceManagerSUBVO;